import { Rng, hashString } from '../../core/rng';
import type { AccountSave, HavenState } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import { type Cost, canAfford, countOf, pay } from '../../game/inventory';
import {
  CREW,
  CROP_BY_ID,
  GREAT_CRAFT_CHANCE,
  HAVEN_LEVEL_XP,
  HAVEN_MAX_LEVEL,
  type HavenBonusKey,
  type Hazard,
  MAX_BATCHES,
  MAX_CRAFT_QUEUE,
  MISSION_BY_ID,
  type MissionDef,
  RECIPE_BY_ID,
  RECRUITS,
  RESEARCH,
  RESEARCH_BY_ID,
  type RecipeDef,
  type ResearchDef,
  XP,
} from './data';

/**
 * Haven Isle rules as pure functions over `AccountSave.haven`. Every
 * time-dependent function takes `now` (ms) so tests can inject a clock; the
 * game passes `Date.now()`.
 */

type HavenHolder = { haven: HavenState };
export type Result = { ok: true } | { ok: false; reason: string };
const fail = (reason: string): Result => ({ ok: false, reason });
const OK: Result = { ok: true };
const MIN = 60_000;

// ================================================================ bonuses & level

/** Sum of a research effect over completed research levels (e.g. 0.2 = 20%). */
export function havenBonus(account: HavenHolder, key: HavenBonusKey): number {
  let total = 0;
  for (const def of RESEARCH) {
    if (def.key !== key) continue;
    total += def.perLevel * Math.min(def.levels.length, account.haven.research[def.id] ?? 0);
  }
  return Math.round(total * 1e6) / 1e6;
}

export function havenLevelFor(xp: number): number {
  let lvl = 1;
  for (let i = 1; i < HAVEN_LEVEL_XP.length; i++) if (xp >= (HAVEN_LEVEL_XP[i] ?? Infinity)) lvl = i + 1;
  return lvl;
}

/** XP progress inside the current level: { into, need } (need = 0 at max level). */
export function havenLevelProgress(xp: number): { level: number; into: number; need: number } {
  const level = havenLevelFor(xp);
  if (level >= HAVEN_MAX_LEVEL) return { level, into: 0, need: 0 };
  const lo = HAVEN_LEVEL_XP[level - 1] ?? 0;
  const hi = HAVEN_LEVEL_XP[level] ?? lo;
  return { level, into: xp - lo, need: hi - lo };
}

/** Adds Haven XP; returns the new level when it went up, else null. */
export function addHavenXp(h: HavenState, xp: number): number | null {
  const before = havenLevelFor(h.xp);
  h.xp += Math.max(0, Math.round(xp));
  h.level = havenLevelFor(h.xp);
  return h.level > before ? h.level : null;
}

// ================================================================ crew

/** Adds crew recruited by Dispatch Logistics that are missing from the roster. */
export function ensureCrew(h: HavenState): string[] {
  const want = RECRUITS.slice(0, h.research.dispatch_logistics ?? 0);
  const added = want.filter((n) => !h.crew.includes(n));
  h.crew.push(...added);
  return added;
}

export function busyCrew(h: HavenState): Set<string> {
  return new Set(h.dispatches.flatMap((d) => d.crew));
}

// ================================================================ research

export const researchLevel = (h: HavenState, id: string): number => h.research[id] ?? 0;

export type ResearchStatus = 'maxed' | 'active' | 'locked' | 'busy' | 'available';

export function researchStatus(h: HavenState, id: string): { status: ResearchStatus; reason?: string } {
  const def = RESEARCH_BY_ID[id];
  if (!def) return { status: 'locked', reason: 'Unknown project' };
  if (h.researching?.id === id) return { status: 'active' };
  if (researchLevel(h, id) >= def.levels.length) return { status: 'maxed' };
  if (havenLevelFor(h.xp) < def.havenLevel) return { status: 'locked', reason: `Requires Haven Lv ${def.havenLevel}` };
  for (const req of def.requires ?? []) {
    if (researchLevel(h, req.id) < req.level) return { status: 'locked', reason: `Requires ${RESEARCH_BY_ID[req.id]?.name ?? req.id} ${roman(req.level)}` };
  }
  if (h.researching) return { status: 'busy', reason: 'The lab is busy' };
  return { status: 'available' };
}

export function researchCost(def: ResearchDef, level: number): Cost & { minutes: number } {
  const L = def.levels[Math.max(0, Math.min(def.levels.length - 1, level - 1))] as ResearchDef['levels'][number];
  return { silver: L.silver, items: { ...L.items }, minutes: L.minutes };
}

/** Pays for and starts the next level of a research project. */
export function startResearch(a: AccountSave, c: CharacterSave, id: string, now: number): Result {
  const def = RESEARCH_BY_ID[id];
  if (!def) return fail('Unknown project');
  const st = researchStatus(a.haven, id);
  if (st.status !== 'available') return fail(st.reason ?? (st.status === 'maxed' ? 'Already complete' : 'In progress'));
  const cost = researchCost(def, researchLevel(a.haven, id) + 1);
  if (!pay(c, a, cost)) return fail('Not enough materials or silver');
  a.haven.researching = { id, doneAt: now + cost.minutes * MIN };
  return OK;
}

// ================================================================ workshop

export function recipeUnlocked(h: HavenState, r: RecipeDef): boolean {
  return havenLevelFor(h.xp) >= r.havenLevel;
}

/**
 * Concrete materials for `batches` of a recipe: fixed inputs plus each pool
 * filled from the largest stacks first. Null when the bag can't cover it.
 */
export function resolveInputs(c: CharacterSave, r: RecipeDef, batches: number): Record<string, number> | null {
  const need: Record<string, number> = {};
  for (const [id, n] of Object.entries(r.items)) need[id] = (need[id] ?? 0) + n * batches;
  for (const pool of r.pools ?? []) {
    let left = pool.count * batches;
    const avail = pool.ids.map((id) => ({ id, n: countOf(c, id) - (need[id] ?? 0) })).sort((x, y) => y.n - x.n);
    for (const s of avail) {
      if (left <= 0) break;
      const take = Math.min(left, Math.max(0, s.n));
      if (take > 0) need[s.id] = (need[s.id] ?? 0) + take;
      left -= take;
    }
    if (left > 0) return null;
  }
  for (const [id, n] of Object.entries(need)) if (countOf(c, id) < n) return null;
  return need;
}

/** Most batches the player can currently afford (capped at MAX_BATCHES). */
export function maxBatches(a: AccountSave, c: CharacterSave, r: RecipeDef): number {
  let n = 0;
  while (n < MAX_BATCHES) {
    const items = resolveInputs(c, r, n + 1);
    if (!items || !canAfford(c, a, { silver: r.silver * (n + 1), items })) break;
    n++;
  }
  return n;
}

/** Seconds for `batches` of a recipe after Journeyman Benches. */
export function craftSeconds(a: HavenHolder, r: RecipeDef, batches: number): number {
  return Math.max(1, Math.round(r.seconds * batches * (1 - havenBonus(a, 'craftSpeed'))));
}

/** Pays for and queues an order; orders run one after another. */
export function queueCraft(a: AccountSave, c: CharacterSave, recipeId: string, batches: number, now: number): Result {
  const r = RECIPE_BY_ID[recipeId];
  if (!r) return fail('Unknown recipe');
  const h = a.haven;
  if (!recipeUnlocked(h, r)) return fail(`Requires Haven Lv ${r.havenLevel}`);
  const n = Math.floor(batches);
  if (n < 1 || n > MAX_BATCHES) return fail('Invalid quantity');
  if (h.crafting.length >= MAX_CRAFT_QUEUE) return fail('The workshop queue is full');
  const items = resolveInputs(c, r, n);
  if (!items || !pay(c, a, { silver: r.silver * n, items })) return fail('Not enough materials or silver');
  const last = h.crafting[h.crafting.length - 1];
  const start = Math.max(now, last?.doneAt ?? 0);
  h.crafting.push({ recipe: r.id, count: n, doneAt: start + craftSeconds(a, r, n) * 1000 });
  return OK;
}

/** Start time of queue entry `i` (the previous entry's end, or its own duration back). */
export function craftStart(a: HavenHolder, i: number): number {
  const q = a.haven.crafting;
  const e = q[i];
  if (!e) return 0;
  const prev = q[i - 1];
  if (prev) return prev.doneAt;
  const r = RECIPE_BY_ID[e.recipe];
  return e.doneAt - (r ? craftSeconds(a, r, e.count) * 1000 : 0);
}

// ================================================================ dispatch

export interface MissionOdds {
  /** Crew fit (can exceed 1: overflow feeds Great Success). */
  adaptation: number;
  success: number;
  great: number;
  countered: Hazard[];
}

export function missionOdds(a: HavenHolder, m: MissionDef, crew: readonly string[]): MissionOdds {
  const countered = m.hazards.filter((hz) => crew.some((n) => CREW[n]?.counters === hz));
  const extra = Math.max(0, crew.length - m.crew);
  const adaptation = 0.45 + 0.55 * (countered.length / Math.max(1, m.hazards.length)) + 0.08 * extra + havenBonus(a, 'dispatchSuccess');
  return { adaptation, success: Math.max(0.1, Math.min(1, adaptation)), great: Math.max(0, Math.min(0.5, adaptation - 0.9)), countered };
}

/** Suggests a crew: one free member per hazard they counter, then any free hands up to the requirement. */
export function autoCrew(h: HavenState, m: MissionDef): string[] {
  const busy = busyCrew(h);
  const free = h.crew.filter((n) => !busy.has(n));
  const out: string[] = [];
  for (const hz of m.hazards) {
    const n = free.find((x) => CREW[x]?.counters === hz && !out.includes(x));
    if (n) out.push(n);
  }
  for (const n of free) {
    if (out.length >= m.crew) break;
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

export function missionUnlocked(h: HavenState, m: MissionDef): boolean {
  return havenLevelFor(h.xp) >= m.havenLevel;
}

export function startDispatch(a: AccountSave, missionId: string, crew: string[], now: number): Result {
  const m = MISSION_BY_ID[missionId];
  const h = a.haven;
  if (!m) return fail('Unknown mission');
  if (!missionUnlocked(h, m)) return fail(`Requires Haven Lv ${m.havenLevel}`);
  if (h.dispatches.some((d) => d.mission === missionId)) return fail('A crew is already on this mission');
  const unique = [...new Set(crew)];
  if (unique.length < m.crew) return fail(`Needs ${m.crew} crew`);
  const busy = busyCrew(h);
  for (const n of unique) {
    if (!h.crew.includes(n)) return fail(`${n} is not in your crew`);
    if (busy.has(n)) return fail(`${n} is already away`);
  }
  h.dispatches.push({ mission: m.id, crew: unique, doneAt: now + m.minutes * MIN });
  return OK;
}

export interface HavenReward {
  items: { id: string; count: number }[];
  silver: number;
  gold: number;
  cards: string[];
}

export type DispatchOutcome = 'fail' | 'success' | 'great';

/** Deterministic mission result (seeded by mission, crew and return time). */
export function dispatchOutcome(a: HavenHolder, d: { mission: string; crew: string[]; doneAt: number }): { outcome: DispatchOutcome; reward: HavenReward } {
  const m = MISSION_BY_ID[d.mission];
  const reward: HavenReward = { items: [], silver: 0, gold: 0, cards: [] };
  if (!m) return { outcome: 'fail', reward };
  const rng = new Rng(hashString(`${d.mission}:${d.doneAt}:${d.crew.join(',')}`));
  const odds = missionOdds(a, m, d.crew);
  const roll = rng.next();
  const outcome: DispatchOutcome = roll < odds.great ? 'great' : roll < odds.success ? 'success' : 'fail';
  const R = m.rewards;
  if (outcome === 'fail') {
    // The crew limps home with scraps.
    const first = R.items[0];
    if (first) reward.items.push({ id: first.id, count: Math.max(1, Math.floor(first.min * 0.3)) });
    reward.silver = Math.round(R.silver[0] * 0.25);
    return { outcome, reward };
  }
  const k = outcome === 'great' ? 1.5 : 1;
  for (const it of R.items) {
    const n = Math.ceil(rng.int(it.min, it.max) * k);
    if (n > 0) reward.items.push({ id: it.id, count: n });
  }
  reward.silver = Math.round(rng.int(R.silver[0], R.silver[1]) * k);
  if (R.gold) reward.gold = Math.round(rng.int(R.gold[0], R.gold[1]) * k);
  if (R.card && R.card.pool.length && rng.next() < Math.min(1, R.card.chance * k)) reward.cards.push(rng.pick(R.card.pool));
  return { outcome, reward };
}

// ================================================================ garden

export interface GardenView {
  crop: string;
  /** 0..1 growth. */
  pct: number;
  /** 0 Seeded, 1 Sprouting, 2 Budding, 3 Ready. */
  stage: number;
  readyAt: number;
}

export function gardenView(h: HavenState, now: number): GardenView | null {
  const g = h.garden;
  if (!g) return null;
  const crop = CROP_BY_ID[g.crop];
  const total = (crop?.minutes ?? 1) * MIN;
  const pct = Math.max(0, Math.min(1, (now - g.plantedAt) / total));
  const stage = pct >= 1 ? 3 : pct >= 0.6 ? 2 : pct >= 0.25 ? 1 : 0;
  return { crop: g.crop, pct, stage, readyAt: g.plantedAt + total };
}

export function plantCrop(a: AccountSave, cropId: string, now: number): Result {
  const crop = CROP_BY_ID[cropId];
  const h = a.haven;
  if (!crop) return fail('Unknown crop');
  if (h.garden) return fail('The bed is already planted');
  if (havenLevelFor(h.xp) < crop.havenLevel) return fail(`Requires Haven Lv ${crop.havenLevel}`);
  if (a.wallet.silver < crop.seeds) return fail('Not enough silver for seeds');
  a.wallet.silver -= crop.seeds;
  h.garden = { crop: crop.id, plantedAt: now };
  return OK;
}

/** Harvests a ripe garden: clears the bed and returns the crop (with Fertile Terraces). */
export function harvestGarden(a: AccountSave, now: number): { items: { id: string; count: number }[]; xp: number; level: number | null } | null {
  const h = a.haven;
  const v = gardenView(h, now);
  const crop = v ? CROP_BY_ID[v.crop] : undefined;
  if (!v || v.stage < 3 || !crop) return null;
  const mult = 1 + havenBonus(a, 'gardenYield');
  h.garden = null;
  const xp = XP.harvest(crop.minutes);
  const level = addHavenXp(h, xp);
  return { items: crop.yields.map((y) => ({ id: y.id, count: Math.max(1, Math.round(y.count * mult)) })), xp, level };
}

// ================================================================ timers

export type HavenEvent =
  | { type: 'research'; id: string; level: number; recruits: string[] }
  | { type: 'craft'; recipe: string; item: string; count: number; great: boolean }
  | { type: 'dispatch'; mission: string; crew: string[]; outcome: DispatchOutcome; reward: HavenReward }
  | { type: 'level'; level: number };

/**
 * Completes every timer due at `now`: research levels, workshop orders (in
 * queue order) and returning dispatches. Mutates the Haven state and returns
 * what happened; the caller hands out the rewards.
 */
export function tickHaven(a: AccountSave, now: number): HavenEvent[] {
  const h = a.haven;
  const out: HavenEvent[] = [];
  let xp = 0;
  ensureCrew(h);
  if (h.researching && h.researching.doneAt <= now) {
    const { id } = h.researching;
    const def = RESEARCH_BY_ID[id];
    h.researching = null;
    if (def) {
      const level = Math.min(def.levels.length, researchLevel(h, id) + 1);
      h.research[id] = level;
      xp += XP.research(researchCost(def, level).minutes);
      out.push({ type: 'research', id, level, recruits: ensureCrew(h) });
    }
  }
  while (h.crafting[0] && h.crafting[0].doneAt <= now) {
    const e = h.crafting.shift() as HavenState['crafting'][number];
    const r = RECIPE_BY_ID[e.recipe];
    if (!r) continue;
    const great = new Rng(hashString(`${e.recipe}:${e.doneAt}:${e.count}`)).next() < GREAT_CRAFT_CHANCE;
    const base = r.out.count * e.count;
    out.push({ type: 'craft', recipe: r.id, item: r.out.id, count: great ? Math.ceil(base * 1.5) : base, great });
    xp += XP.craftBatch * e.count;
  }
  for (const d of [...h.dispatches]) {
    if (d.doneAt > now) continue;
    h.dispatches.splice(h.dispatches.indexOf(d), 1);
    const m = MISSION_BY_ID[d.mission];
    const res = dispatchOutcome(a, d);
    out.push({ type: 'dispatch', mission: d.mission, crew: d.crew, outcome: res.outcome, reward: res.reward });
    const full = XP.dispatch(m?.minutes ?? 10);
    xp += res.outcome === 'fail' ? Math.round(full / 2) : full;
  }
  const level = addHavenXp(h, xp);
  if (level) out.push({ type: 'level', level });
  return out;
}

/** Earliest pending timer (ms), for "next event" readouts; null when idle. */
export function nextHavenTimer(h: HavenState): number | null {
  const times = [h.researching?.doneAt, h.crafting[0]?.doneAt, ...h.dispatches.map((d) => d.doneAt)].filter((t): t is number => typeof t === 'number');
  return times.length ? Math.min(...times) : null;
}

// ================================================================ formatting

export function roman(n: number): string {
  return ['', 'I', 'II', 'III', 'IV', 'V'][n] ?? String(n);
}

/** "4m 05s" / "1h 02m" / "12s". */
export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}
